import { useState } from 'react';
import dayjs from 'dayjs';
import type { PrjSchedule } from 'models/setup/PrjSchedule';
import {
  calculateTimelineDragIndicator,
  getDropTimeByY,
  type DragOverIndicatorInfo,
} from 'utils/CalendarUtils';

interface UseTimelineDndProps {
  schedules?: PrjSchedule[];
  onMoveScheduleDate: (
    schdId: string,
    targetDateStr: string,
    targetTime?: string,
    isAllDay?: boolean
  ) => void;
}

export function useTimelineDnd({ schedules, onMoveScheduleDate }: UseTimelineDndProps) {
  const [draggingDuration, setDraggingDuration] = useState<number>(60);
  const [dragOverTimeline, setDragOverTimeline] = useState<{
    dayStr: string;
    info: DragOverIndicatorInfo;
  } | null>(null);
  const [dragOverAllDayDate, setDragOverAllDayDate] = useState<string | null>(null);
  const [dragSourceInfo, setDragSourceInfo] = useState<{
    sch: PrjSchedule;
    sourceDayStr?: string;
    isAllDay?: boolean;
  } | null>(null);

  const handleDragStart = (
    e: React.DragEvent,
    sch: PrjSchedule,
    sourceDayStr?: string,
    isAllDay?: boolean
  ) => {
    if (!sch.schdId || sch.schdStatCd === 'CPLT') {
      e.preventDefault();
      return;
    }
    e.dataTransfer.setData('text/plain', sch.schdId);
    if (sourceDayStr) {
      e.dataTransfer.setData('application/x-source-day', sourceDayStr);
    }
    if (isAllDay !== undefined) {
      e.dataTransfer.setData('application/x-is-allday', isAllDay ? 'true' : 'false');
    }
    e.dataTransfer.effectAllowed = 'move';

    const st = dayjs(sch.planStDtm);
    const en = dayjs(sch.planEndDtm);
    const duration = Math.max(en.diff(st, 'minute'), 30);
    setDraggingDuration(duration);
    setDragSourceInfo({ sch, sourceDayStr, isAllDay });
  };

  const handleDragEnd = () => {
    setDragOverTimeline(null);
    setDragOverAllDayDate(null);
    setDragSourceInfo(null);
  };

  const handleTimelineDragOver = (e: React.DragEvent, dayStr: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const rect = e.currentTarget.getBoundingClientRect();
    const info = calculateTimelineDragIndicator(e.clientY, rect, draggingDuration);
    setDragOverTimeline({ dayStr, info });
  };

  const handleTimelineDragLeave = (e: React.DragEvent, dayStr: string) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      if (dragOverTimeline?.dayStr === dayStr) {
        setDragOverTimeline(null);
      }
    }
  };

  const handleTimelineDrop = (e: React.DragEvent, dayStr: string) => {
    e.preventDefault();
    const schdId = e.dataTransfer.getData('text/plain') || dragSourceInfo?.sch.schdId;
    const currentIndicator = dragOverTimeline?.dayStr === dayStr ? dragOverTimeline.info : null;
    setDragOverTimeline(null);

    const targetSch = dragSourceInfo?.sch || schedules?.find((s) => s.schdId === schdId);
    const wasAllDay = dragSourceInfo?.isAllDay;
    setDragSourceInfo(null);

    if (!schdId) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const timeStr = currentIndicator
      ? currentIndicator.snappedTimeStr
      : getDropTimeByY(e.clientY, rect);

    // drop한 위치가 처음과 같을 때 아무런 동작을 안 하도록 검사
    if (targetSch && targetSch.planStDtm) {
      const origStart = dayjs(targetSch.planStDtm);
      const origStartDate = origStart.format('YYYY-MM-DD');
      const origStartTime = origStart.format('HH:mm');

      // 종일 일정이 아니었고, 날짜와 시작 시간(분 단위)이 동일한 위치에 drop한 경우 무동작 처리
      if (!wasAllDay && dayStr === origStartDate && timeStr === origStartTime) {
        return;
      }
    }

    onMoveScheduleDate(schdId, dayStr, timeStr, false);
  };

  const handleAllDayDragOver = (e: React.DragEvent, dayStr: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverAllDayDate !== dayStr) {
      setDragOverAllDayDate(dayStr);
    }
  };

  const handleAllDayDragLeave = (e: React.DragEvent, dayStr: string) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      if (dragOverAllDayDate === dayStr) {
        setDragOverAllDayDate(null);
      }
    }
  };

  const handleAllDayDrop = (e: React.DragEvent, dayStr: string) => {
    e.preventDefault();
    setDragOverAllDayDate(null);
    const schdId = e.dataTransfer.getData('text/plain') || dragSourceInfo?.sch.schdId;
    const sourceDayStr =
      e.dataTransfer.getData('application/x-source-day') || dragSourceInfo?.sourceDayStr;
    const targetSch = dragSourceInfo?.sch || schedules?.find((s) => s.schdId === schdId);
    setDragSourceInfo(null);

    if (!schdId) return;

    // drop한 위치가 처음과 같을 때 아무런 동작을 안 하도록 검사
    if (targetSch && targetSch.planStDtm) {
      const origStartDate = dayjs(targetSch.planStDtm).format('YYYY-MM-DD');
      // 원래 시작일과 같거나, 드래그를 시작한 날짜 셀과 같은 경우 무동작 처리
      if (dayStr === origStartDate || (sourceDayStr && dayStr === sourceDayStr)) {
        return;
      }
    }

    onMoveScheduleDate(schdId, dayStr, undefined, true);
  };

  const getTimelineIndicator = (dayStr: string): DragOverIndicatorInfo | null => {
    return dragOverTimeline?.dayStr === dayStr ? dragOverTimeline.info : null;
  };

  const isAllDayDragOver = (dayStr: string): boolean => {
    return dragOverAllDayDate === dayStr;
  };

  return {
    handleDragStart,
    handleDragEnd,
    handleTimelineDragOver,
    handleTimelineDragLeave,
    handleTimelineDrop,
    handleAllDayDragOver,
    handleAllDayDragLeave,
    handleAllDayDrop,
    getTimelineIndicator,
    isAllDayDragOver,
  };
}
